import React, { useEffect } from 'react';
import { ArrowLeft, FileText, Users, BarChart2, Layers, Target, RefreshCw, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const DesignMaturityCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = [
    {
      id: 1,
      title: "Structured Intake",
      desc: "Goals • Purpose • Use Cases",
      icon: <FileText className="w-6 h-6 text-accent" />,
      detail: "Shifted conversations from features to outcomes by standardizing requirements before design work started."
    },
    {
      id: 2,
      title: "Research & Alignment",
      desc: "Brainstorming • Knowledge Base",
      icon: <Users className="w-6 h-6 text-accent" />,
      detail: "Created a shared research knowledge base so insights were reusable across products."
    },
    {
      id: 3,
      title: "Design Execution",
      desc: "Flows • UI • Heuristic Alignment",
      icon: <Layers className="w-6 h-6 text-accent" />,
      detail: "Moved beyond just shipping interfaces to crafting strategic, heuristic-aligned user flows."
    },
    {
      id: 4,
      title: "Design System",
      desc: "Components • Patterns • Standards",
      icon: <CheckCircle className="w-6 h-6 text-accent" />,
      detail: "Transitioned from a component library to a full design system to ensure consistency and speed."
    },
    {
      id: 5,
      title: "Measurement",
      desc: "Usage Analytics • Funnels",
      icon: <BarChart2 className="w-6 h-6 text-accent" />,
      detail: "Built analytics dashboards to evaluate design decisions based on real user behavior."
    },
    {
      id: 6,
      title: "Accountability",
      desc: "KPIs • Benchmarks",
      icon: <Target className="w-6 h-6 text-accent" />,
      detail: "Attached KPIs to every new requirement, feeding learnings back into future designs."
    }
  ];

  return (
    <div className="bg-pastel-bg min-h-screen font-sans text-neutral-900 selection:bg-accent/20">
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-pastel-bg/90 backdrop-blur-md border-b border-neutral-200/50">
        <div className="container mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-neutral-600 hover:text-accent transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
      </nav>

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl">
          
          {/* Header */}
          <header className="mb-24">
            <h1 className="text-4xl md:text-6xl font-serif text-neutral-900 mb-8 leading-tight">
              Building Design Maturity
            </h1>
            <div className="bg-white p-8 rounded-[2rem] border border-neutral-100 shadow-sm">
              <p className="text-xl text-neutral-600 leading-relaxed mb-6">
                I joined the analytics team as the <strong className="text-neutral-900">first designer</strong>. 
                There was no established design practice—design was treated as a delivery step rather than a strategic function.
              </p>
              <p className="text-xl text-neutral-900 font-serif">
                My goal was to build design maturity within the team, not just ship interfaces.
              </p>
            </div>
          </header>

          {/* Visual Framework */}
          <section className="mb-32">
            <div className="flex items-center justify-between mb-12">
               <h2 className="text-3xl font-serif">The Maturity Framework</h2>
               <div className="flex items-center gap-2 text-sm text-neutral-500 bg-white px-4 py-2 rounded-full border border-neutral-100">
                 <RefreshCw className="w-4 h-4 text-accent" /> Continuous Feedback Loop
               </div>
            </div>

            {/* Horizontal Process Visualization */}
            <div className="relative">
              {/* Connecting Line */}
              <div className="absolute top-1/2 left-0 w-full h-1 bg-neutral-100 -z-10 hidden md:block rounded-full"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
                {steps.map((step, index) => (
                  <motion.div 
                    key={step.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white p-6 rounded-2xl border border-neutral-100 shadow-sm flex flex-col h-full relative group hover:-translate-y-1 transition-transform duration-300"
                  >
                    <div className="bg-pastel-cream w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:bg-accent/10 transition-colors">
                      {step.icon}
                    </div>
                    <div className="text-xs font-bold text-accent mb-2 uppercase tracking-wider">Step 0{step.id}</div>
                    <h3 className="font-bold text-neutral-900 mb-2">{step.title}</h3>
                    <p className="text-xs text-neutral-500 font-medium">{step.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Detailed Narrative */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-24">
            
            <div className="space-y-12">
              <div>
                <h3 className="text-2xl font-serif mb-4 flex items-center gap-3">
                  <span className="bg-neutral-900 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  Structure at Entry
                </h3>
                <p className="text-neutral-600 leading-relaxed bg-white p-6 rounded-2xl border border-neutral-100">
                  I introduced a <strong className="text-neutral-900">standardized requirement format</strong> that clarified goals, purpose, use cases, and success metrics before any design work started. This shifted conversations from features to outcomes.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-serif mb-4 flex items-center gap-3">
                  <span className="bg-neutral-900 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  Research Embedded
                </h3>
                <p className="text-neutral-600 leading-relaxed bg-white p-6 rounded-2xl border border-neutral-100">
                  We ran structured use-case discussions and brainstorming sessions and created a <strong className="text-neutral-900">shared research knowledge base</strong> so insights were reusable across products.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-serif mb-4 flex items-center gap-3">
                  <span className="bg-neutral-900 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  Data-Driven Design
                </h3>
                <p className="text-neutral-600 leading-relaxed bg-white p-6 rounded-2xl border border-neutral-100">
                  To make design measurable, I introduced <strong className="text-neutral-900">quantitative usage analytics</strong>, building dashboards and funnel flows to evaluate decisions based on real user behavior.
                </p>
              </div>
            </div>

            <div className="space-y-12 md:mt-24">
               <div>
                <h3 className="text-2xl font-serif mb-4 flex items-center gap-3">
                  <span className="bg-neutral-900 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                  Scaling Consistency
                </h3>
                <p className="text-neutral-600 leading-relaxed bg-white p-6 rounded-2xl border border-neutral-100">
                  As scale increased, inconsistency became a risk. I transitioned the team from a component library to a <strong className="text-neutral-900">full design system</strong>, enabling standardization and reducing rework.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-serif mb-4 flex items-center gap-3">
                  <span className="bg-neutral-900 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">5</span>
                  Closing the Loop
                </h3>
                <p className="text-neutral-600 leading-relaxed bg-white p-6 rounded-2xl border border-neutral-100">
                  Finally, I attached <strong className="text-neutral-900">KPIs to every new requirement</strong>, setting clear benchmarks and feeding learnings back into future designs.
                </p>
              </div>
            </div>

          </section>

          {/* Outcome Footer */}
          <section className="bg-accent/5 p-12 rounded-[2.5rem] text-center border border-accent/10">
            <h2 className="text-2xl md:text-3xl font-serif text-neutral-900 mb-4">The Evolution</h2>
            <p className="text-xl text-neutral-700 max-w-2xl mx-auto leading-relaxed">
              Over time, design evolved into a <span className="text-accent font-bold">scalable, data-informed, and system-driven practice</span> within the analytics team.
            </p>
          </section>

        </div>
      </main>
    </div>
  );
};

export default DesignMaturityCaseStudy;
